import React, { Children, useEffect, useRef, useState } from 'react';
import { useTheme } from '@emotion/react';
import { ArrowLeftIcon, ArrowRightIcon } from '@ui-kit-ngs/icons';
import { getRandomAriaId } from '../../utils';
import { useUIKitTranslator } from '@ui-kit-ngs/providers';
import { ScrollArrowEnum, ScrollOverflowEnum } from '../scrollable/constants';
import ScrollList from '../scrollable/StyledScrollList';
import useHorizontalScroll from '../scrollable/useHorizontalScroll';
import { CAROUSEL_ARROW_WIDTH, CAROUSEL_OVERFLOW_FADE_WIDTH, } from './constants';
import CarouselArrow from './StyledCarouselArrow';
import CarouselContainer from './StyledCarouselContainer';
import CarouselContentWrapper from './StyledCarouselContentWrapper';
import CarouselItem from './StyledCarouselItem';
import Separator from './StyledSeparator';
// eslint-disable-next-line complexity
export const CarouselComponent = ({ children, touchable, activeElement, light, stretched, id, ariaLabel, customItemsStyles, onScroll, itemsTapHighlight = true, bilateral = false, }) => {
    const carouselId = id || `${getRandomAriaId()}-carousel`;
    const t = useUIKitTranslator();
    const scrollRef = useRef(null);
    const [overflow, setOverflow] = useState(ScrollOverflowEnum.NONE);
    const lastKnownScrollPos = useRef(0);
    const timer = useRef(undefined);
    const theme = useTheme();
    const { getScrollLeft, getOverflow, getScrollContainerWidth, scrollTo, } = useHorizontalScroll({
        scrollRef,
        onScrollHandler: (e) => {
            if (onScroll)
                onScroll(e);
            const currentOverflow = getOverflow();
            setOverflow(currentOverflow);
            lastKnownScrollPos.current = e.currentTarget.scrollLeft;
            clearTimeout(timer.current);
            timer.current = setTimeout(() => {
                scrollTo.start({ x: lastKnownScrollPos.current, immediate: true });
            }, 250);
        },
    });
    const displayArrows = theme.device !== 'mobile' &&
        !touchable &&
        overflow !== ScrollOverflowEnum.NONE;
    const onArrowClick = (arrow) => {
        const scrollLeft = getScrollLeft();
        const scrollContainerWidth = getScrollContainerWidth();
        const direction = arrow === ScrollArrowEnum.LEFT ? -1 : 1;
        scrollTo.start({
            x: scrollLeft + scrollContainerWidth * direction,
            immediate: false,
            config: {
                duration: 500,
            }
        });
    };
    // Re-calculate overflow and assign carouselItems refs
    const carouselItemsRef = useRef([]);
    useEffect(() => {
        const currentOverflow = getOverflow();
        setOverflow(currentOverflow);
        carouselItemsRef.current = carouselItemsRef.current.slice(0, children.length);
    }, [children]);
    // Scroll to active element on activeElement change
    useEffect(() => {
        const activeElementRef = carouselItemsRef.current[activeElement];
        if (activeElementRef) {
            const activeElementOffset = activeElementRef.offsetLeft;
            const activeElementWidth = activeElementRef.clientWidth;
            const scrollContainerWidth = getScrollContainerWidth();
            const scrollContainerLeft = getScrollLeft();
            const arrowsOffset = displayArrows ? 0 : CAROUSEL_ARROW_WIDTH * 2;
            const fadeOffset = CAROUSEL_OVERFLOW_FADE_WIDTH / 2;
            const activeElementOverflow = activeElementOffset + activeElementWidth + fadeOffset + arrowsOffset;
            if (activeElementOverflow > (scrollContainerWidth + scrollContainerLeft)) {
                const offset = activeElementOverflow - scrollContainerWidth;
                scrollTo.start({ x: offset, immediate: true });
            }
            if (activeElementOffset < scrollContainerLeft) {
                const offset = Math.max(0, activeElementOffset - fadeOffset - arrowsOffset);
                scrollTo.start({ x: offset, immediate: true });
            }
        }
    }, [activeElement, children]);
    const renderArrows = () => (React.createElement(React.Fragment, null,
        React.createElement(CarouselArrow, { key: "arrow-left", "data-testid": "Carousel.LeftArrow", onClick: () => onArrowClick(ScrollArrowEnum.LEFT), disabled: overflow === ScrollOverflowEnum.RIGHT, light: light },
            React.createElement(ArrowLeftIcon, { fill: "currentColor", height: "12" })),
        React.createElement(Separator, null),
        React.createElement(CarouselArrow, { key: "arrow-right", "data-testid": "Carousel.RightArrow", onClick: () => onArrowClick(ScrollArrowEnum.RIGHT), disabled: overflow === ScrollOverflowEnum.LEFT, light: light },
            React.createElement(ArrowRightIcon, { fill: "currentColor", height: "12" }))));
    return (React.createElement(CarouselContainer, { light: light, "data-testid": "Carousel.Container", stretched: stretched },
        displayArrows && bilateral && (React.createElement(CarouselArrow, { key: "arrow-left", "data-testid": "Carousel.LeftArrow.Bilateral", onClick: () => onArrowClick(ScrollArrowEnum.LEFT), disabled: overflow === ScrollOverflowEnum.RIGHT, light: light },
            React.createElement(ArrowLeftIcon, { fill: "currentColor", height: "12" }))),
        React.createElement(CarouselContentWrapper, { overflow: overflow, "data-testid": "Carousel.ContentWrapper" },
            React.createElement(ScrollList, { ref: scrollRef, id: carouselId, role: "menubar", "aria-label": ariaLabel !== null && ariaLabel !== void 0 ? ariaLabel : t('carouselAriaLabel'), "data-testid": "Carousel.Scroll" }, Children.map(children, (child, index) => (React.createElement(CarouselItem, { customStyles: customItemsStyles, tapHighlight: itemsTapHighlight, isActive: activeElement === index, key: index, "data-testid": `Carousel.Item-${index}`, "data-active": activeElement === index ? true : undefined, light: light, ref: (el) => {
                    carouselItemsRef.current[index] = el;
                }, role: "menuitem", tabIndex: 0, stretched: stretched }, child))))),
        displayArrows && bilateral && (React.createElement(CarouselArrow, { key: "arrow-right", "data-testid": "Carousel.RightArrow.Bilateral", onClick: () => onArrowClick(ScrollArrowEnum.RIGHT), disabled: overflow === ScrollOverflowEnum.LEFT, light: light },
            React.createElement(ArrowRightIcon, { fill: "currentColor", height: "12" }))),
        displayArrows && !bilateral && renderArrows()));
};
CarouselComponent.defaultProps = {
    touchable: false,
    light: false,
    strechted: false,
};
export default CarouselComponent;
